/*
 * Scrolling helpers for the one-page layout.
 *
 * The home page is the whole site: Sobre, Produtos and Amaré are sections of
 * `/` rather than routes of their own, and the navbar moves between them by
 * scrolling. Two things have to be handled for that to feel right, and both
 * live here so the navbar and the page agree on them.
 *
 * Offsets are not computed in JS. Every target carries
 * `scroll-margin-top: var(--nav-offset)` (see global.css), which the platform
 * applies to `scrollIntoView` and to plain anchor jumps alike — so a link
 * followed from another page lands in the same place a nav click does.
 */

/* Sections in the order they appear on the page. `home` is the hero, and is
   the one entry that scrolls to the top of the document rather than to an
   element — the navbar reads that off `id === HOME_ID`. */
export const HOME_ID = "home"

/*
 * The footer: a scroll target like the others without being a section of the
 * page. While the contact form is switched off it carries the address, the
 * phone, the email and WhatsApp — it is how people get in touch (see
 * site-config.js), so it is what "Contato" in the nav points at.
 *
 * Last in the list, so the navbar marks Contato rather than Amaré as the place
 * you are once the page is scrolled to the bottom.
 */
export const FOOTER_ID = "contato"

export const SECTIONS = [
  { id: HOME_ID, label: "Home" },
  { id: "sobre", label: "Sobre" },
  { id: "produtos", label: "Produtos" },
  { id: "amare", label: "Amaré" },
  { id: FOOTER_ID, label: "Contato" },
]

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

/* True on the home page, where the sections exist. Anywhere else — /contact/,
   or one of the legacy routes — a nav item has to navigate instead. Written
   against the trailing slash Gatsby serves so `/` and `` both count. */
export function isOnePage() {
  if (typeof window === "undefined") return false
  return window.location.pathname.replace(/\/+$/, "") === ""
}

/*
 * Whether a section is in the current document. False is the navbar's signal
 * to let the browser follow the link rather than swallowing the click — on
 * Contato, "Produtos" has to navigate.
 */
export function hasSection(id) {
  if (typeof window === "undefined") return false
  return id === HOME_ID || document.getElementById(id) !== null
}

/*
 * How long a jump keeps its aim on the section after the scroll starts, and how
 * long it leaves the scroll alone before it is allowed to correct anything.
 *
 * The grace covers the frame or two between asking for a smooth scroll and the
 * browser starting it, during which the page is still perfectly still and would
 * otherwise read as "arrived".
 */
const SETTLE_MS = 2000
const SETTLE_GRACE_MS = 150

/* How often a scroll still in flight may be re-aimed. Each one restarts the
   animation, so this is loose enough that a run of images landing together
   cannot turn the scroll into a stutter. */
const REAIM_MS = 120

/*
 * What counts as the reader taking over.
 *
 * Input events rather than a change in scroll position: a smooth scroll moves
 * the page every frame, so from the outside our own scrolling and theirs look
 * exactly the same. These all fire before the `click` that starts a jump, so a
 * handler attached during that click never sees the interaction that caused it.
 */
const TAKEOVER = ["wheel", "pointerdown", "keydown"]

/* The hold in progress, so a second jump replaces the first rather than the two
   of them pulling the page towards different sections. */
let releaseHold = null

function stopHold() {
  if (releaseHold) releaseHold()
}

/*
 * Hold a section against the top of the viewport while the page settles.
 *
 * The images in the sections carry no width or height in the markup, so they
 * take up no room at all until they arrive. A jump made while images above the
 * target are still landing is aimed at an offset that is about to move: each
 * one that resolves pushes the target further down the document, and the scroll
 * runs to the offset it was given and stops there — short of the section, with
 * the bottom of the previous one still on screen.
 *
 * `scrollIntoView` takes its aim once and cannot be told the page moved, so the
 * aim is kept for it. While the scroll is still travelling the target is
 * recomputed and the animation retargeted, which costs nothing to look at — it
 * simply comes to rest in the right place. Anything that lands after that is
 * put right in a single step, which is visible, but by then it is a few pixels
 * rather than the height of a photograph.
 *
 * Either way it stops at the first sign of the reader scrolling for themselves,
 * and in any case once the page has had SETTLE_MS to finish loading.
 *
 * The durable fix is for the images to declare their size, so that nothing
 * moves in the first place; this keeps the nav honest until they do.
 */
function holdSection(el, behavior) {
  const started = Date.now()
  let raf = null
  let timer = null
  let last = null
  let still = 0
  let reaimed = 0

  const stop = () => {
    if (raf !== null) window.cancelAnimationFrame(raf)
    if (timer !== null) window.clearTimeout(timer)
    raf = null
    timer = null
    TAKEOVER.forEach(type => window.removeEventListener(type, stop))
    if (releaseHold === stop) releaseHold = null
  }

  const tick = () => {
    /* Gone from the document — a route change inside the settle window took
       the sections with it. Chasing a detached element would read every offset
       as zero and walk the new page back to the top. */
    if (!el.isConnected) return stop()

    const y = Math.round(window.scrollY)
    if (y === last) still += 1
    else {
      still = 0
      last = y
    }

    /* Where the platform would have put it: the top of the viewport, less
       whatever the section reserves for the navbar. */
    const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0
    const drift = Math.round(el.getBoundingClientRect().top - margin)
    const now = Date.now()

    if (drift !== 0) {
      if (still >= 3 && now - started > SETTLE_GRACE_MS) {
        /* At rest, so put it right in one step. Three still frames is the
           scroll having finished; correcting before that would land on top of
           a running animation and cancel it where it stood. */
        const before = Math.round(window.scrollY)
        window.scrollBy({ top: drift, behavior: "auto" })

        /* An instant scroll lands synchronously, so a position that has not
           moved means the page has no more to give: the target is as close to
           the top as the document allows. The footer is the everyday case —
           it is the last thing on the page and there is nothing below it to
           scroll up into view. Asking again every frame would achieve exactly
           as much for the rest of the settle window. */
        if (Math.round(window.scrollY) === before) return stop()

        last = null
        still = 0
      } else if (still < 3 && now - reaimed > REAIM_MS) {
        /* Still travelling. Re-aiming retargets the animation in flight, so it
           comes to rest in the right place instead of arriving short and then
           hopping. Throttled, because each one restarts the animation.
           `scrollTo` at an offset we work out ourselves, rather than another
           `scrollIntoView`: a second scrollIntoView for an element already
           being scrolled to is taken as the request already in progress and
           changes nothing. */
        reaimed = now
        window.scrollTo({ top: window.scrollY + drift, behavior })
      }
    }

    raf = window.requestAnimationFrame(tick)
  }

  releaseHold = stop
  TAKEOVER.forEach(type => window.addEventListener(type, stop, { passive: true }))
  timer = window.setTimeout(stop, SETTLE_MS)
  raf = window.requestAnimationFrame(tick)
}

/*
 * Scroll to a section by id. Returns false when the target is not there.
 */
export function scrollToSection(id, { smooth = true } = {}) {
  if (!hasSection(id)) return false

  /* Whatever the last jump was still aiming at, this one replaces it. */
  stopHold()

  const behavior = smooth && !prefersReducedMotion() ? "smooth" : "auto"

  if (id === HOME_ID) {
    /* Nothing to hold: the top of the document stays where it is however much
       the page below it grows. */
    window.scrollTo({ top: 0, behavior })
    return true
  }

  const el = document.getElementById(id)
  el.scrollIntoView({ behavior, block: "start" })
  holdSection(el, behavior)
  return true
}

/*
 * Land on the section named by the URL hash after the page mounts.
 *
 * The browser's own hash handling has already run and found nothing by the
 * time React renders, so arriving at /#produtos from /contact/ needs the jump
 * repeating. It is repeated once more after `load` because the sections are
 * full of images with no intrinsic size in the markup: the first jump is
 * measured against a document that is still short, and every image that
 * resolves below the target pushes it away.
 *
 * The second jump is skipped if the reader has moved in the meantime —
 * `scrollY` is compared against where the first one left them, so a page they
 * have already started reading is never yanked back.
 */
export function landOnHash() {
  if (typeof window === "undefined") return undefined

  const id = window.location.hash.replace("#", "")
  if (!id) return undefined

  let settled = null

  const jump = () => {
    if (!scrollToSection(id, { smooth: false })) return
    settled = Math.round(window.scrollY)
  }

  /* One frame's grace so the section has been laid out at all. */
  const frame = window.requestAnimationFrame(jump)

  const realign = () => {
    if (settled === null) return
    if (Math.abs(Math.round(window.scrollY) - settled) > 2) return
    jump()
  }

  window.addEventListener("load", realign)
  return () => {
    window.cancelAnimationFrame(frame)
    window.removeEventListener("load", realign)
  }
}
